import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ZODIAC_SIGNS, type ZodiacSignId } from '@yadviga/shared'
import { api, type RetrogradePeriodPayload } from '../../api/client'
import { useSession } from '../../store/user.store'
import { PageHeader } from '../../components/ui/PageHeader'
import { LoadingDots } from '../../components/ui/LoadingDots'
import { ErrorState } from '../../components/ui/ErrorState'
import { MarkdownText } from '../../components/ui/Markdown'

type PlanetFilter = 'all' | 'mercury' | 'venus' | 'mars' | 'jupiter' | 'saturn'
const FILTERS: readonly PlanetFilter[] = ['all', 'mercury', 'venus', 'mars', 'jupiter', 'saturn'] as const

export function AstroRetrogradeRoute() {
  const { t } = useTranslation()
  const user = useSession((s) => s.user)
  const lang = (user?.language ?? 'ru') as 'ru' | 'en'
  const [filter, setFilter] = useState<PlanetFilter>('all')

  const query = useQuery({
    queryKey: ['retrogrades', 12],
    queryFn: () => api.retrogrades(12),
    staleTime: 1000 * 60 * 60 * 24,
  })

  const periods = query.data
  const filtered = filter === 'all' ? periods : periods?.filter((p) => p.planet === filter)

  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader
        title={t('astro.retrograde_title')}
        subtitle={t('astro.retrograde_intro')}
        backTo="/astro"
      />

      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={clsx(
              'rounded-full px-3 py-1.5 text-sm transition active:scale-[0.97]',
              f === filter ? 'bg-sage-800 text-cream' : 'bg-sage-100 text-sage-700 hover:bg-sage-200',
            )}
          >
            {t(`astro.retrograde_planet.${f}` as never)}
          </button>
        ))}
      </div>

      {query.isLoading && <LoadingDots compact />}
      {query.isError && <ErrorState onRetry={() => query.refetch()} />}
      {filtered && filtered.length === 0 && (
        <p className="rounded-2xl bg-white/60 p-5 text-center text-sm text-sage-700 ring-1 ring-sage-200/60">
          {t('astro.retrograde_none')}
        </p>
      )}
      {filtered && filtered.length > 0 && (
        <ul className="flex flex-col gap-3">
          {filtered.map((p, i) => (
            <PeriodCard key={`${p.planet}-${p.startDate}-${i}`} period={p} lang={lang} />
          ))}
        </ul>
      )}
    </div>
  )
}

function PeriodCard({ period, lang }: { period: RetrogradePeriodPayload; lang: 'ru' | 'en' }) {
  const { t } = useTranslation()
  return (
    <li className="flex flex-col gap-3 rounded-2xl bg-white/70 p-5 ring-1 ring-sage-200/60">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-serif text-lg text-sage-800">
          {t(`astro.retrograde_planet.${period.planet}` as never)} R
        </h3>
        <span className="rounded-full bg-sage-100 px-2.5 py-1 text-[10px] uppercase tracking-wider text-sage-700">
          {signLabel(period.startSign, lang)}
        </span>
      </div>
      <p className="text-xs text-sage-500">
        {formatRange(period.startDate, period.endDate, lang)} · {t('astro.retrograde_days', { n: period.durationDays })}
      </p>
      <MarkdownText>{period.description[lang]}</MarkdownText>
    </li>
  )
}

function signLabel(id: ZodiacSignId, lang: 'ru' | 'en'): string {
  const s = ZODIAC_SIGNS.find((z) => z.id === id)
  if (!s) return id
  return lang === 'en' ? s.nameEn : s.nameRu
}

function formatRange(startIso: string, endIso: string, lang: 'ru' | 'en'): string {
  const fmt = new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  return `${fmt.format(new Date(startIso))} — ${fmt.format(new Date(endIso))}`
}
